// © Thibault Sabathier, 2026. frontend/src/components/messaging/MessagingErrorBoundary.js
//
// Confinement de la messagerie flottante.
//
// POURQUOI
// La messagerie est un widget monté par-dessus toute l'application. Quand son
// rendu échoue, l'ErrorBoundary global remonte jusqu'à la racine et remplace la
// page entière par l'écran d'erreur : l'utilisateur croit que la plateforme est
// cassée alors que seul le dock l'est. C'est ce qui s'est produit sur
// serenity99 le 27/08 (NotFoundError insertBefore pendant une conversation).
//
// Ce boundary-ci arrête l'erreur au niveau du widget. L'application reste
// utilisable, l'erreur part quand même dans Sentry avec un tag dédié, et
// l'utilisateur peut relancer la messagerie sans recharger la page.

import React from 'react'
import PropTypes from 'prop-types'
import { captureException } from '../../utils/sentry'
import i18n from '../../i18n'

class MessagingErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
    // Liaison dans le constructeur plutôt qu'un champ de classe : la config
    // ESLint du projet est en ecmaVersion 2020 et ne sait pas parser les
    // champs de classe (c'est ce qui rend ErrorBoundary.js illisible au lint).
    this.handleRetry = this.handleRetry.bind(this)
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    console.error('[Messagerie] rendu interrompu:', error)
    captureException(error, {
      tags: { source: 'floating-messaging' },
      contexts: { react: { componentStack: errorInfo?.componentStack } },
    })
  }

  handleRetry() {
    this.setState({ hasError: false })
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="messaging-crash-pill" role="alert">
        <span>{i18n.t('messaging:error_boundary.title')}</span>
        <button type="button" onClick={this.handleRetry}>
          {i18n.t('messaging:error_boundary.retry')}
        </button>
      </div>
    )
  }
}

MessagingErrorBoundary.propTypes = {
  children: PropTypes.node,
}

export default MessagingErrorBoundary
